import React from 'react';
import { Page } from '../../../layout';
import CheckPointContent from './CheckPointContent';
import ResponsiveTabMenu from '../../../shared/components/ResponsiveTabMenu';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ApolloError from '../../../shared/components/ApolloError';
import { Breadcrumb } from '../../../shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '../../../shared/services/pageTitleService';
import GET_ORG_UNIT_INFO from '../../../shared/queries/getOrgUnitInfoQuery';
import { getDataNotFoundPage } from '../../../shared/services/getDataNotFoundPageService';
import { userTier } from '../../../../apollo/cache';
import { canUserAccessSeveralOrganization } from '../../../shared/services/userAccessibilityService';
import OrgUnitBreadcrumbAnnotation from '../../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import GET_CHECK_POINTS from '../../queries/getCheckPoints';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';

const ViewCheckPointPage = () => {
  const { orgUnitId, selectedAppraisalId, checkPointId } = useValuesFromParams();
  const tier = useReactiveVar(userTier);

  const navigate = useNavigate();

  const {
    data: orgUnitCheckPoints,
    loading,
    error
  } = useCacheNetworkQuery(GET_CHECK_POINTS, {
    variables: { orgUnitId: orgUnitId }
  });

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });
  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const submittedCheckPoint = getSubmittedCheckPoint(orgUnitCheckPoints, selectedAppraisalId);
  const checkPointTabs = getTabs(submittedCheckPoint);

  const pageTitle = getPageTitle([orgUnitName, ' View Check Point']);
  const commonItems = [
    renderedOrgUnit,
    `Appraisal ${selectedAppraisalId}`,
    getCheckPointDataCollectionDate(checkPointId, submittedCheckPoint)
  ];
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, ...commonItems]
    : commonItems;

  const breadcrumbPathItems = [
    { name: 'Check Points', url: `/check-points/${orgUnitId}` },
    { name: 'View Check Point', url: '' }
  ];

  const onTabChange = newCheckPointId => {
    navigate(
      `/check-points/${orgUnitId}/view-check-point/${selectedAppraisalId}/${newCheckPointId}`
    );
  };

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (error || orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='check-point-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading || loading}
      />

      <ResponsiveTabMenu
        activeTab={checkPointId}
        tabs={checkPointTabs}
        onChange={newCheckPointId => onTabChange(newCheckPointId)}
        loading={loading}
      />
      <CheckPointContent
        orgUnitId={orgUnitId}
        checkPointId={checkPointId}
        appraisalId={selectedAppraisalId}
        dataCollectionDate={getCheckPointDataCollectionDate(checkPointId, submittedCheckPoint)}
      />
    </Page>
  );
};

const useValuesFromParams = () => {
  const { orgUnit, appraisal, checkPoint } = useParams();
  return {
    orgUnitId: parseInt(orgUnit),
    selectedAppraisalId: parseInt(appraisal),
    checkPointId: parseInt(checkPoint)
  };
};

const getSubmittedCheckPoint = (orgUnitCheckPoints, appraisalId) => {
  if (!orgUnitCheckPoints) {
    return;
  }

  return orgUnitCheckPoints.checkPoints.AppraisalOrgUnits.find(
    aou => aou.Appraisal.Id === appraisalId
  ).CheckPoints.filter(cp => !cp.IsDraft);
};

const getCheckPointDataCollectionDate = (checkPointId, checkPoints) => {
  if (!checkPoints) {
    return;
  }

  return checkPoints.find(cp => cp.Id === checkPointId)?.DataCollectionDate;
};

const getTabs = checkPoints => {
  if (!checkPoints) {
    return [];
  }

  const tabs = checkPoints.map(cp => ({
    value: cp.Id,
    displayName: cp.DataCollectionDate
  }));

  return tabs;
};

export default ViewCheckPointPage;
